import { Left, Right } from '@tunasong/icons'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useId } from 'react'
import styles from './ghost-bar.module.css'
import { ghostAnnouncement, ghostPosition, isPositionShown, type GhostState } from './ghost-options.js'

/**
 * The ghost options' bar (melody toolkit M4 step 4.2; spec §6, §10, §11): "Writing options…" with three empty slots
 * and `Discard` while the request runs, then `‹ Option n of total ›` with the option's label, `Play this option`
 * (`Stop` while it plays), `Keep` and `Discard`; on failure, the failure line and `Try again`.
 *
 * The live region is always rendered, empty when idle, so the first announcement is heard.
 */

const writingText = 'Writing options…'
const notCountedText = "That didn't work, and it wasn't counted against your AI use."
/** Joni's review; approved by Brian on the user's delegation, 2026-10-29 */
const replacesText = 'Keep replaces your melody.'
/** A transform changes some notes, so it says how many (Joni; approved by Brian, 2026-10-31) */
const replacesCountText = (count: number) => (count === 1 ? 'Keep replaces 1 note.' : `Keep replaces ${count} notes.`)

export interface GhostBarProps {
  state: GhostState
  onPrevious: () => void
  onNext: () => void
  onPlay: () => void
  /** Stops the option playing: offered in place of Play this option while `isOptionPlaying` */
  onStop: () => void
  onKeep: () => void
  onDiscard: () => void
  onRetry: () => void
  /** Why Try again can't run, shown beside it; null or absent when it can (e.g. "Needs a connection.") */
  retryDisabledReason?: string | null
  /** The melody already has notes, so Keep replaces them: said beside Keep */
  isReplacing?: boolean
  /** A transform's option: how many written notes Keep changes or removes; absent for a whole-line option */
  replacedCount?: number
  /** The option shown is what is playing (GH #2978) */
  isOptionPlaying?: boolean
  /** The live region's id, so the roll group can be described by it (Joni's M4 audit) */
  liveRegionId?: string
  className?: string
}

const announcementOf = (state: GhostState) => {
  if (state.status === 'writing') {
    return writingText
  }
  if (state.status === 'failed') {
    return state.isNotCounted ? `${state.line} ${notCountedText}` : state.line
  }
  return ghostAnnouncement(state)
}

export const GhostBar: FC<GhostBarProps> = props => {
  const {
    state,
    onPrevious,
    onNext,
    onPlay,
    onStop,
    onKeep,
    onDiscard,
    onRetry,
    retryDisabledReason,
    isReplacing,
    replacedCount,
    isOptionPlaying,
    liveRegionId,
    className,
  } = props
  const replacesId = useId()
  const reasonId = useId()
  const lineId = useId()
  /** With no key or chords the reason is the failure line itself: show it once, and let the line describe Try again */
  const isReasonTheLine = state.status === 'failed' && retryDisabledReason === state.line
  const retryDescribedBy = retryDisabledReason ? (isReasonTheLine ? lineId : reasonId) : undefined
  const rootClass = className ? `${styles.bar} ${className}` : styles.bar

  return (
    <div className={state.status === 'idle' ? styles.idle : rootClass} data-testid="ghost-bar">
      {/* Visually hidden: what the bar shows, read once per change (spec §10) */}
      <div id={liveRegionId} role="status" aria-live="polite" className={styles.liveRegion}>
        {announcementOf(state)}
      </div>

      {state.status === 'writing' ? (
        <div className={styles.row}>
          {/* The live region reads the text once; the slots are only a picture of what is coming */}
          <span className={styles.text} aria-hidden="true">
            {writingText}
          </span>
          {[0, 1, 2].map(slot => (
            <span key={slot} className={styles.slot} aria-hidden="true" data-testid="ghost-slot" />
          ))}
          {/* GH #2978: a way out that needs no Esc in the roll; a late answer is dropped by the reducer */}
          <span className={styles.actions}>
            <Button size="small" onClick={onDiscard}>
              Discard
            </Button>
          </span>
        </div>
      ) : null}

      {state.status === 'showing' ? (
        <div className={styles.row}>
          {state.options.length > 1 ? (
            <IconButton size="small" aria-label="Previous option" onClick={onPrevious}>
              <Left fontSize="small" />
            </IconButton>
          ) : null}
          {isPositionShown(state) ? (
            <span className={styles.position} aria-hidden="true">
              {ghostPosition(state)}
            </span>
          ) : null}
          {state.options.length > 1 ? (
            <IconButton size="small" aria-label="Next option" onClick={onNext}>
              <Right fontSize="small" />
            </IconButton>
          ) : null}
          {state.options[state.index]?.label ? (
            <span className={styles.label} aria-hidden="true">
              {state.options[state.index]?.label}
            </span>
          ) : null}
          <span className={styles.actions}>
            <Button size="small" onClick={isOptionPlaying ? onStop : onPlay}>
              {isOptionPlaying ? 'Stop' : 'Play this option'}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={onKeep}
              aria-describedby={isReplacing ? replacesId : undefined}
            >
              Keep
            </Button>
            {isReplacing ? (
              <span id={replacesId} className={`${styles.reason} ${styles.replaces}`}>
                {replacedCount === undefined ? replacesText : replacesCountText(replacedCount)}
              </span>
            ) : null}
            <Button size="small" onClick={onDiscard}>
              Discard
            </Button>
          </span>
        </div>
      ) : null}

      {state.status === 'failed' ? (
        <div className={styles.row}>
          <span id={lineId} className={styles.text} aria-hidden="true">
            {state.line}
            {state.isNotCounted ? <span className={styles.notCounted}> {notCountedText}</span> : null}
          </span>
          <span className={styles.actions}>
            <Button
              size="small"
              onClick={retryDisabledReason ? undefined : onRetry}
              aria-disabled={retryDisabledReason ? 'true' : undefined}
              aria-describedby={retryDescribedBy}
            >
              Try again
            </Button>
            {retryDisabledReason && !isReasonTheLine ? (
              <span id={reasonId} className={styles.reason}>
                {retryDisabledReason}
              </span>
            ) : null}
            <Button size="small" onClick={onDiscard}>
              Discard
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  )
}
